import { G } from '../core/math';

/**
 * Direct-drive kart tuning. Every number the driving model reads lives here.
 *
 * The defining choice is REAR BRAKES ONLY, as on a direct-drive kart (no
 * differential, solid rear axle, chain straight off the engine — not a
 * shifter, which is required to run a front brake too). It caps braking at
 * roughly 0.6 g of the kart's own tyre grip (a downforce car manages 4 g)
 * which makes braking zones long and legible, and it means brake pressure
 * steals grip from the axle that keeps the kart pointing straight — brake
 * hard mid-corner and it spins.
 */

export const MASS = 170; // kg, kart + driver
export const WHEELBASE = 1.05; // m
export const REAR_WEIGHT_BIAS = 0.58; // karts sit the driver well back
/** Rear track width — the two rear wheels' centre-to-centre distance. Used
 *  for the lateral load-transfer and diff-less scrub terms below; it has no
 *  effect on the longitudinal (braking/traction) model. */
export const REAR_TRACK = 1.4; // m
/** Half the kart's own physical footprint, for track-limit sampling — how far
 *  a wheel sits from the point the physics tracks as `kart.x, kart.y`.
 *  Deliberately independent of the visual sprite size in entities.ts, which
 *  is drawn larger on purpose so the kart reads at a playable zoom. */
export const VEHICLE_HALF_WIDTH = 0.65; // m

/** CG to front axle. Larger when the mass sits rearward. */
export const A_CG = WHEELBASE * REAR_WEIGHT_BIAS;
/** CG to rear axle. */
export const B_CG = WHEELBASE - A_CG;

/** Yaw inertia, dumbbell approximation about the two axles. */
export const YAW_INERTIA = MASS * A_CG * B_CG;

/** Very low — a kart chassis is inches off the ground. Limits weight transfer. */
export const CG_HEIGHT = 0.28;

export const MAX_STEER = 0.42; // rad, ~24deg; kart steering is fast and direct
export const STEER_RATE = 4.5; // rad/s of steering actuation

/**
 * Slip angle at which a tyre reaches its friction limit. Front saturates first,
 * so the kart understeers at steady state — forgiving. The rear ellipse
 * (see vehicle.ts) is what takes that stability away under braking.
 */
export const PEAK_SLIP_FRONT = 0.14; // rad
export const PEAK_SLIP_REAR = 0.16; // rad

export const ENGINE_POWER = 23000; // W, shifter-class rather than Cadet/Junior
export const DRAG_K = 0.45; // N/(m/s)^2, open bodywork and an upright driver
export const ROLLING_RESIST = 0.015 * MASS * G; // N

/** A direct-drive kart has no gearbox, so revs are speed itself, rescaled —
 *  no clutch or ratio to slip. Shared by the HUD's RPM readout and the
 *  engine note so both agree on what the engine is doing at a given speed. */
export const IDLE_RPM = 2200;
export const REDLINE_RPM = 12500;

/**
 * Direct-drive engine braking. Off throttle and off brake, a chain straight to
 * the axle (no clutch, no freewheel) still drags against compression — unlike
 * a car that coasts. Modelled as a speed-proportional decel so it fades to
 * nothing at a stop rather than holding the kart on a slope.
 */
export const ENGINE_BRAKE_DECEL_PER_MS = 0.09; // (m/s^2) per (m/s) of speed

/** Solid rear axle: no differential, so power-on scrubs the kart straight. */
export const YAW_DAMP_BASE = 0.8;
export const YAW_DAMP_THROTTLE = 0.7;

/** Below LOWSPEED_MAX the slip-angle model is blended out for a kinematic one. */
export const LOWSPEED_MAX = 3.0; // m/s
/** Denominator floor in the slip-angle atan2, keeps it finite at a standstill. */
export const SLIP_DENOM_FLOOR = 2.0; // m/s

/** Lock-up engages above rear capacity, releases at 85% — hysteresis band. */
export const LOCKUP_ENGAGE = 1.0;
export const LOCKUP_RELEASE = 0.85;
/* Sliding grip once locked is per-mode; see `lockedGrip` in realism.ts. */

/**
 * Everything the vehicle model needs to know about what it is driving on.
 *
 * Kept separate from `Surface` so callers can fold in weather and tyre state
 * before handing it over — the physics should not need to know why grip is what
 * it is, only what it currently is.
 */
export interface GripPatch {
  readonly mu: number;
  readonly extraDrag: number; // N
}

export interface Surface extends GripPatch {
  readonly name: 'track' | 'kerb' | 'grass' | 'gravel' | 'runoffAsphalt';
}

// Kart slicks, not a road tyre: real kart rubber peaks around 1.8-2.2g.
const TRACK_MU = 1.85;
export const SURFACES: Record<Surface['name'], Surface> = {
  track: { name: 'track', mu: TRACK_MU, extraDrag: 0 },
  kerb: { name: 'kerb', mu: 1.47, extraDrag: 60 },
  grass: { name: 'grass', mu: 0.7, extraDrag: 420 },
  /** A trap, not a margin — lower grip than grass and much harder drag to
   *  claw back out of. See docs/circuit-fidelity.md §6. */
  gravel: { name: 'gravel', mu: 0.55, extraDrag: 900 },
  /** Real paved run-off on the circuits that have it (Paul Ricard's blue/red
   *  zones chief among them) — full grip, no penalty, which is the point:
   *  see docs/circuit-fidelity.md §6.2 for the design consequence that comes
   *  with authoring it. Shares `track`'s mu rather than a second hardcoded
   *  figure so the two can never drift apart. */
  runoffAsphalt: { name: 'runoffAsphalt', mu: TRACK_MU, extraDrag: 0 },
};

/**
 * The realism modes' yaw-authority gains (stability assist's corrective
 * torque, the solid axle's own scrub damping) were tuned and pinned by
 * `tests/realism.test.ts` against a 1.45g track — the exact spins and
 * recoveries there are calibrated numbers, not derived ones. Grip scales the
 * *driving* side of a slide (more mu means more force, means more yaw
 * moment) automatically through capRear; it does nothing to the
 * *counteracting* side, which would otherwise leave every mode more
 * spin-prone than it was tuned to be purely because the tyres got stickier.
 * Scaling both damping-family gains by the same ratio grip moved keeps the
 * original balance.
 */
const YAW_TUNING_BASELINE_MU = 1.45;
export const YAW_AUTHORITY_SCALE = SURFACES.track.mu / YAW_TUNING_BASELINE_MU;

/** Peak longitudinal force the rear tyres can put down on clean track, with no
 *  weight transfer folded in. `stepKart`'s own per-tick `capRear` is the real,
 *  transfer-aware ceiling the vehicle actually drives against — see the note
 *  by the throttle branch in vehicle.ts. This static figure now exists only as
 *  a conservative reference for the telemetry friction-circle display. */
export const REFERENCE_REAR_LOAD = MASS * G * REAR_WEIGHT_BIAS;
export const MAX_TRACTION_FORCE = SURFACES.track.mu * REFERENCE_REAR_LOAD;

/**
 * Rear grip available while braking, once the load the braking itself transfers
 * off the rear axle is accounted for. Solved as a fixed point: the harder you
 * brake, the less rear load there is to brake with.
 */
export function dynamicRearCapacity(mu: number): number {
  return (mu * REFERENCE_REAR_LOAD) / (1 + (mu * CG_HEIGHT) / WHEELBASE);
}

/**
 * Sized so a fully-pressed pedal sits just *under* the straight-line limit.
 *
 * The brake is a single key that ramps to full and stays there, so anything less
 * than this would make lock-up the default state rather than a mistake — which
 * is exactly what it was before: every corner of a clean reference lap reported
 * locked rears.
 *
 * Holding the brake therefore gives threshold braking, and locking comes from
 * the combined friction ellipse instead: it is *adding steering to the brakes*
 * that breaks the rears loose. That is the behaviour the game is about, and it
 * is what "brake early and straight, trail off as you turn in" actually means.
 *
 * The exact figure matters more than it looks. At 0.97 the pedal consumed
 * almost the whole grip circle and left roughly a quarter of its radius for
 * cornering — barely 0.15 g — so *any* steering input under brakes locked, and
 * trail braking, the very technique this is built to reward, was impossible.
 * At 0.85 there is about half the circle left, which is enough to turn in
 * gently on the brakes while a mid-corner stamp still lets go.
 */
const BRAKE_LOCK_HEADROOM = 0.85;
export const MAX_BRAKE_FORCE = dynamicRearCapacity(SURFACES.track.mu) * BRAKE_LOCK_HEADROOM;

/** Physics runs at a fixed rate; rendering interpolates between ticks. */
export const TICK_HZ = 120;
export const TICK_DT = 1 / TICK_HZ;

/** Rearward push while holding the brake at a standstill, m/s^2. Gentle — a
 *  recovery tool to reverse off a barrier, not a way to lap backwards. */
export const REVERSE_ACCEL = 4.0;
/** Max rearward speed, m/s. */
export const REVERSE_MAX_SPEED = 4.0;
